/**
 * Sync Wagamour server + client tier cache after RevenueCat purchase or restore.
 */

'use client'

import { getSession } from 'next-auth/react'
import {
  REVENUECAT_ENTITLEMENT_PACK_LEADER,
  SUBSCRIPTION_STATUS_UPDATING_USER_MESSAGE,
} from '@/lib/monetization/iapConstants'
import { logUpgradeIapTrace } from '@/lib/monetization/upgradeIapTrace'

export type RefreshSubscriptionResult =
  | { ok: true; tier: 'pack_leader' | 'free'; serverSynced: boolean }
  | { ok: false; error: string; serverSynced: false }

export const PACK_LEADER_TIER_APPLIED_EVENT = 'wagamour:packLeaderTierApplied'
const OPTIMISTIC_PACK_LEADER_TIER_KEY = 'wagamour_optimistic_pack_leader_tier'

export function readOptimisticPackLeaderTier(): boolean {
  try {
    return localStorage.getItem(OPTIMISTIC_PACK_LEADER_TIER_KEY) === '1'
  } catch {
    return false
  }
}

export function clearOptimisticPackLeaderTier(): void {
  try {
    localStorage.removeItem(OPTIMISTIC_PACK_LEADER_TIER_KEY)
  } catch {
    /* non-fatal */
  }
}

/** Optimistic Pack Leader unlock for UI + React state while server sync catches up. */
export function applyPackLeaderTierLocally(source = 'unknown'): {
  writeOk: boolean
  before: { planTier?: unknown; subscriptionTier?: unknown }
  after: { planTier: 'PACK_LEADER'; subscriptionTier: 'pack_leader' }
} {
  const after = { planTier: 'PACK_LEADER' as const, subscriptionTier: 'pack_leader' as const }
  let before: { planTier?: unknown; subscriptionTier?: unknown } = {}
  let writeOk = false

  try {
    const raw = localStorage.getItem('wagamour_user_profile')
    const parsed = (raw ? JSON.parse(raw) : {}) as Record<string, unknown>
    before = {
      planTier: parsed.planTier,
      subscriptionTier: parsed.subscriptionTier,
    }
    parsed.planTier = after.planTier
    parsed.subscriptionTier = after.subscriptionTier
    localStorage.setItem('wagamour_user_profile', JSON.stringify(parsed))
    localStorage.setItem(OPTIMISTIC_PACK_LEADER_TIER_KEY, '1')
    writeOk = true
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent(PACK_LEADER_TIER_APPLIED_EVENT, {
          detail: { ...after, source },
        })
      )
    }
  } catch {
    writeOk = false
  }

  logUpgradeIapTrace('local pack_leader tier cache applied', {
    source,
    before,
    after,
    writeOk,
  })

  return { writeOk, before, after }
}

/** @deprecated Prefer applyPackLeaderTierLocally — updates React state via event. */
export function applyLocalPackLeaderTierCache(): void {
  applyPackLeaderTierLocally('applyLocalPackLeaderTierCache')
}

async function refreshSessionBestEffort(): Promise<void> {
  try {
    await getSession()
  } catch {
    /* session refresh best-effort */
  }
}

async function syncSubscriptionWithServer(): Promise<RefreshSubscriptionResult> {
  const syncRes = await fetch('/api/subscription/revenuecat/sync', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  })

  const syncBody = (await syncRes.json().catch(() => ({}))) as {
    success?: boolean
    tier?: 'pack_leader' | 'free'
    error?: string
    message?: string
    retry?: boolean
  }

  if (!syncRes.ok || syncBody.success !== true) {
    logUpgradeIapTrace('verification failed reason', {
      stage: 'server_revenuecat_sync',
      httpStatus: syncRes.status,
      error: syncBody.error ?? null,
      message: syncBody.message ?? null,
      retry: syncBody.retry ?? null,
      expectedEntitlementId: REVENUECAT_ENTITLEMENT_PACK_LEADER,
    })
    return {
      ok: false,
      error: SUBSCRIPTION_STATUS_UPDATING_USER_MESSAGE,
      serverSynced: false,
    }
  }

  const tier = syncBody.tier === 'pack_leader' ? 'pack_leader' : 'free'

  if (tier === 'pack_leader') {
    clearOptimisticPackLeaderTier()
  }

  try {
    const raw = localStorage.getItem('wagamour_user_profile')
    if (raw) {
      const parsed = JSON.parse(raw) as Record<string, unknown>
      parsed.planTier = tier === 'pack_leader' ? 'PACK_LEADER' : 'FREE'
      parsed.subscriptionTier = tier
      localStorage.setItem('wagamour_user_profile', JSON.stringify(parsed))
    }
  } catch {
    /* non-fatal */
  }

  await refreshSessionBestEffort()

  logUpgradeIapTrace('server subscription sync succeeded', { tier })
  return { ok: true, tier, serverSynced: true }
}

/** Blocking sync — used when caller has no client entitlement proof. */
export async function refreshSubscriptionStateAfterIap(): Promise<RefreshSubscriptionResult> {
  try {
    return await syncSubscriptionWithServer()
  } catch (error) {
    console.error('[refreshSubscriptionStateAfterIap]', error)
    return {
      ok: false,
      error: SUBSCRIPTION_STATUS_UPDATING_USER_MESSAGE,
      serverSynced: false,
    }
  }
}

/** Non-blocking server sync after StoreKit/RevenueCat client confirms entitlement. */
export function syncSubscriptionWithServerBestEffort(): Promise<RefreshSubscriptionResult> {
  return syncSubscriptionWithServer().catch((error) => {
    console.error('[syncSubscriptionWithServerBestEffort]', error)
    return {
      ok: false,
      error: SUBSCRIPTION_STATUS_UPDATING_USER_MESSAGE,
      serverSynced: false,
    }
  })
}
